import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { Shield, Compass, Users, X, ArrowUpRight } from "lucide-react";
import { IMAGES, COMPANY } from "../data/site";
import { useSiteImages } from "../lib/useSiteImages";

const API = process.env.REACT_APP_BACKEND_URL;
const STRENGTH_ICONS = [Compass, Users, Shield];

const STRENGTHS = [
  { icon: Compass, title: "一貫対応力", desc: "造成・施工・維持管理・不動産開発までを自社で一貫対応。分断のない工程管理で、品質とスピードを両立します。", detail: "工程間の引き継ぎロスや認識のずれが起きないため、品質とスピードを両立できます。土地の状態を最初から知っている私たちが、最後の活用まで責任を持ちます。" },
  { icon: Users, title: "地域密着", desc: "三重県津市を拠点に東海地域へ。この土地の気候・地盤・行政を知り尽くした施工が強みです。", detail: "地盤特性・気候・行政手続きまで熟知した施工で、無駄のない工程と適正価格を実現します。緊急時にも迅速に駆けつけられる距離で対応します。" },
  { icon: Shield, title: "安全最優先", desc: "KY活動の徹底、定期安全大会、全現場の安全パトロール。無事故・無災害を全現場で貫きます。", detail: "重機・高所作業の多い現場だからこそ、安全は全員で守り抜く約束です。資格取得支援や保険加入も含め、仕組みとして安全を支えています。" },
];

function StrengthCards({ items }) {
  const [active, setActive] = useState(null);
  const cards = items.map((s, i) => ({ ...s, icon: STRENGTH_ICONS[i % STRENGTH_ICONS.length] }));

  return (
    <>
      <div className="mt-16 grid gap-px border border-line bg-line md:grid-cols-3">
        {cards.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08} className="bg-ink">
            <button
              type="button"
              data-testid={`strength-card-${i}`}
              onClick={() => setActive({ ...s, num: `0${i + 1}` })}
              className="group h-full w-full cursor-pointer p-8 text-left transition-colors hover:bg-coal"
            >
              <s.icon className="h-8 w-8 text-accent" />
              <p className="mt-6 font-display text-5xl font-extrabold text-steel">0{i + 1}</p>
              <h3 className="mt-3 font-mincho text-xl font-bold text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-400">{s.desc}</p>
              <p className="mt-5 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-accent opacity-0 transition-opacity group-hover:opacity-100">
                詳しく見る
                <ArrowUpRight className="h-3.5 w-3.5" />
              </p>
            </button>
          </Reveal>
        ))}
      </div>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <motion.div
              data-testid="strength-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="clip-corner relative w-full max-w-lg border border-line bg-coal p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                data-testid="strength-modal-close"
                onClick={() => setActive(null)}
                className="absolute right-5 top-5 text-gray-500 transition-colors hover:text-accent"
                aria-label="閉じる"
              >
                <X className="h-6 w-6" />
              </button>
              <active.icon className="h-10 w-10 text-accent" />
              <p className="mt-4 font-display text-5xl font-extrabold text-steel">{active.num}</p>
              <h3 className="mt-2 font-mincho text-2xl font-bold text-white">{active.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-gray-400">{active.desc}</p>
              <p className="mt-6 border-t border-line pt-6 text-sm leading-loose text-gray-300">{active.detail}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const SAFETY = [
  { title: "毎朝のKY（危険予知）活動と安全ミーティングの実施", detail: "その日の作業内容と危険箇所を全員で共有し、指差し呼称で確認してから作業を開始します。不安全行動を未然に防ぐ毎日の積み重ねが、安全の土台です。" },
  { title: "年2回の全社安全大会と定期的な安全パトロール", detail: "全社員が集まる安全大会で事例共有と意識向上を図るほか、管理者による抜き打ちの安全パトロールを定期的に実施し、現場の安全水準を常に点検しています。" },
  { title: "重機オペレーター・技能講習など資格取得の全額補助", detail: "車両系建設機械、玉掛け、高所作業車など、現場で必要な資格・技能講習の費用を会社が全額負担します。技術と知識の習得そのものが、最大の安全対策です。" },
  { title: "第三者賠償保険・建設業総合保険への加入", detail: "万が一の事故に備え、第三者賠償保険・建設業総合保険に加入しています。お客様・近隣の皆様・働く仲間、すべての人を守るための備えです。" },
];

function SafetyCards({ items }) {
  const [active, setActive] = useState(null);

  return (
    <>
      <div className="mt-16 grid gap-4 sm:grid-cols-2">
        {items.map((item, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <button
              type="button"
              data-testid={`safety-item-${i}`}
              onClick={() => setActive({ ...item, num: String(i + 1).padStart(2, "0") })}
              className="group flex h-full w-full cursor-pointer items-start gap-4 border border-line bg-ink p-6 text-left transition-colors hover:border-accent"
            >
              <span className="mt-1 font-display text-xl font-extrabold text-accent">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex-1">
                <span className="block text-sm leading-relaxed text-gray-300">{item.title}</span>
                <span className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-accent opacity-0 transition-opacity group-hover:opacity-100">
                  詳しく見る
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <motion.div
              data-testid="safety-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="clip-corner relative w-full max-w-lg border border-line bg-coal p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                data-testid="safety-modal-close"
                onClick={() => setActive(null)}
                className="absolute right-5 top-5 text-gray-500 transition-colors hover:text-accent"
                aria-label="閉じる"
              >
                <X className="h-6 w-6" />
              </button>
              <p className="font-display text-6xl font-extrabold text-accent">{active.num}</p>
              <h3 className="mt-4 font-mincho text-2xl font-bold text-white">{active.title}</h3>
              <p className="mt-6 border-t border-line pt-6 text-sm leading-loose text-gray-300">{active.detail}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const DEFAULT_PHILOSOPHY = {
  title: "土地の価値を見極め、",
  accent: "百年先のまち",
  end: "をつくる。",
  body: "私たちが整えるのは、目の前の土地だけではありません。その先に建つ建物、集う人々、育まれる暮らし、そして百年先のまちの姿です。一つひとつの現場に誠実に向き合い、次の世代につながる価値をつくり続けます。",
};

const DEFAULT_CEO_MESSAGE = "ネオ建設は、「土地を整え、未来をつくる。」を合言葉に歩んできました。土木工事から始まった私たちの仕事は、今や環境整備、再生可能エネルギー、そして不動産の管理・開発へと広がっています。変わらないのは、土地と真正面から向き合う姿勢です。お客様の大切な資産を預かる責任を胸に、これからも確かな技術と誠実な仕事で、地域の未来をつくってまいります。";

export default function About() {
  const [about, setAbout] = useState(null);
  const siteImages = useSiteImages();

  useEffect(() => {
    fetch(`${API}/api/about`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("fetch failed"))))
      .then(setAbout)
      .catch(() => {});
  }, []);

  const strengths = about?.strengths?.length ? about.strengths : STRENGTHS;
  const safety = about?.safety?.length ? about.safety : SAFETY;
  const philosophy = {
    title: about?.philosophy_title ?? DEFAULT_PHILOSOPHY.title,
    accent: about?.philosophy_accent ?? DEFAULT_PHILOSOPHY.accent,
    end: about?.philosophy_title_end ?? DEFAULT_PHILOSOPHY.end,
    body: about?.philosophy_body ?? DEFAULT_PHILOSOPHY.body,
  };
  const ceoMessage = about?.ceo_message ?? DEFAULT_CEO_MESSAGE;

  return (
    <div data-testid="about-page">
      <PageHero eyebrow="ABOUT US" titleEn="ABOUT" titleJa="私たちについて" desc="土地のプロフェッショナルとして、三重から未来のまちづくりを支える。" image={siteImages.about || IMAGES.structure} />

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="PHILOSOPHY" title="企業理念" />
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-mincho text-2xl font-bold leading-relaxed text-white sm:text-4xl">
                {philosophy.title}
                <br />
                <span className="text-accent">{philosophy.accent}</span>
                {philosophy.end}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-sm leading-loose text-gray-400 sm:text-base">
                {philosophy.body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-coal py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="clip-corner overflow-hidden border border-line">
                <img src={siteImages.ceo || "/ceo.png"} alt="代表取締役 黒崎龍一" loading="lazy" className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="MESSAGE" title="代表挨拶" />
            <Reveal delay={0.1}>
              <p className="mt-8 text-sm leading-loose text-gray-300 sm:text-base">
                {ceoMessage}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 border-t border-line pt-6 font-mincho text-lg font-bold text-white">
                代表取締役　<span className="text-accent">{COMPANY.ceo}</span>
              </p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-gray-600">※ ご挨拶文は正式なものが確定次第、差し替えとなります。</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="STRENGTH" title="ネオ建設の強み" />
          <StrengthCards items={strengths} />
        </div>
      </section>

      <section className="border-t border-line bg-coal py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="SAFETY" title="安全への取り組み" sub="「無事故・無災害」は当たり前の目標ではなく、全員で守り抜く約束です。" />
          <SafetyCards items={safety} />
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
